import { nanoid } from 'nanoid'
import { ReactNode } from 'react'

import { emitter, NotificationAddEvent, NotificationRemoveEvent } from './events'
import { MessageType, NotificationType, NotificationVariant } from './type'

export { NotificationContainer } from './NotificationContainer'

// For backward compatibility
export { NotificationContainer as MessageContainer } from './NotificationContainer'

type NotificationOption = Omit<NotificationType, 'id' | 'variant' | 'title' | 'createdAt'>

// New Notification API
export const notification = {
  show(variant: NotificationVariant, title: ReactNode | string, option?: NotificationOption) {
    const id = nanoid()
    const createdAt = Date.now()

    emitter.emit(NotificationAddEvent, {
      id,
      variant,
      title,
      createdAt,
      ...option,
    })

    return id
  },

  info(title: ReactNode | string, option?: NotificationOption) {
    return this.show('info', title, option)
  },

  success(title: ReactNode | string, option?: NotificationOption) {
    return this.show('success', title, option)
  },

  warning(title: ReactNode | string, option?: NotificationOption) {
    return this.show('warning', title, option)
  },

  error(title: ReactNode | string, option?: NotificationOption) {
    return this.show('error', title, option)
  },

  close(id: string) {
    emitter.emit(NotificationRemoveEvent, id)
  },
}

// Legacy Message API for backward compatibility
type ShowMessageOptionType = Omit<MessageType, 'id' | 'type' | 'content'>

// Map legacy type to notification variant
const typeToVariant: Record<MessageType['type'], NotificationVariant> = {
  default: 'info',
  success: 'success',
  error: 'error',
}

export const message = {
  open({ type, content, action }: Omit<MessageType, 'id'>) {
    const id = nanoid()
    const createdAt = Date.now()
    const variant = typeToVariant[type]

    emitter.emit(NotificationAddEvent, {
      id,
      variant,
      title: content,
      action,
      createdAt,
    })

    return id
  },

  close(id: string) {
    emitter.emit(NotificationRemoveEvent, id)
  },

  show(content: string | ReactNode, option?: ShowMessageOptionType) {
    return this.open({ type: 'default', content, ...option })
  },

  success(content: string | ReactNode, option?: ShowMessageOptionType) {
    return this.open({ type: 'success', content, ...option })
  },

  error(content: string | ReactNode, option?: ShowMessageOptionType) {
    return this.open({ type: 'error', content, ...option })
  },
}
